import { useState, useEffect } from "react";

import { apiRequest } from "../../network/apiClient";
import { API_ENDPOINTS } from "../../api/apiEndpoints";

import { toast } from "react-toastify";

const AddReminderButton = ({ leadID, onReminderAdd }) => {

  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // 🔹 Helper: current local datetime in YYYY-MM-DDTHH:MM
  const getLocalNow = () => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const year = now.getFullYear();
    const month = pad(now.getMonth() + 1);
    const day = pad(now.getDate());
    const hours = pad(now.getHours());
    const minutes = pad(now.getMinutes());
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const [formData, setFormData] = useState({
    title: "",
    details: "",
    datetime: "",
    leadId: leadID || "", // Ensure it's not null initially
    section: "Account"
  });
  
  // Update formData when leadId changes
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      leadId: leadID || "",
      section: "Account"
    }));
  }, [leadID]);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // 🔒 prevent backdated reminder on input change
    if (name === "datetime") {
      const nowLocal = getLocalNow();
      if (value && value < nowLocal) {
        toast.error("Reminder time cannot be in the past.");
        return;
      }
    }

    setFormData({ ...formData, [name]: value });
  };

  const addReminder = async () => {
    const nowLocal = getLocalNow();

    // 🔒 final validation
    if (!formData.datetime) {
      toast.error("Please select a reminder date & time.");
      return;
    }

    if (formData.datetime < nowLocal) {
      toast.error("Reminder time cannot be in the past.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await apiRequest(API_ENDPOINTS.ADD_REMINDER, "POST", formData);
      closeModal();
      toast.success("Reminder Successfully Added");
      onReminderAdd && onReminderAdd();
    } catch (error) {
      toast.error(error || "Error Adding Reminder");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      {/* Button to open the modal */}
      <button
        onClick={openModal}
        className="bg-white text-black py-2 md:py-3 px-3 md:px-4 rounded-2xl md:rounded-3xl border-[1px] border-black hover:bg-yellow-300 transition flex items-center min-h-[44px] text-sm md:text-base"
      >
        <img src="/icons/alarm_add.svg" className="size-5 md:size-6 mr-1 md:mr-2" />
        <b className="whitespace-nowrap">Add Reminder</b>
      </button>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-4 md:p-6 rounded-lg w-full max-w-md md:w-1/3">
            <div className="flex justify-between items-start w-full border-b border-gray-300 mb-4 pb-2">
              <h2 className="text-base md:text-lg font-bold">Add a Reminder</h2>
            </div>
            <input
              type="text"
              name="title"
              placeholder="Title"
              value={formData.title}
              onChange={handleChange}
              className="w-full p-3 mb-3 border rounded text-sm md:text-base min-h-[44px]"
            />
            <textarea
              name="details"
              placeholder="Details"
              value={formData.details}
              onChange={handleChange}
              className="w-full p-3 mb-3 border rounded text-sm md:text-base min-h-[88px]"
              rows={3}
            />
            <input
              type="datetime-local"
              name="datetime"
              value={formData.datetime}
              onChange={handleChange}
              className="w-full p-3 mb-3 border rounded text-sm md:text-base min-h-[44px]"
              min={getLocalNow()}  // 🔒 cannot choose past datetime
            />

            <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-0 sm:space-x-2">
              <button
                onClick={closeModal}
                className="px-4 py-2.5 md:py-3 bg-white border-[1px] border-black rounded-2xl md:rounded-3xl hover:bg-gray-100 text-sm md:text-base font-bold min-h-[44px]"
              >
                Cancel
              </button>
              <button
                onClick={addReminder}
                className={`rounded-2xl md:rounded-3xl px-6 md:px-8 py-2.5 md:py-3 text-black font-bold text-sm md:text-base min-h-[44px] ${
                  isLoading ? "bg-gray-500" : "bg-yellow-300 hover:bg-yellow-400"
                }`}
                disabled={isLoading}
              >
                {isLoading ? "Adding..." : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddReminderButton;
